import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Conversation } from '@/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 子串匹配
export function substringMatch(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

// 子序列匹配
export function subsequenceMatch(text: string, query: string): boolean {
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  let textIndex = 0;
  let queryIndex = 0;
  while (textIndex < textLower.length && queryIndex < queryLower.length) {
    if (textLower[textIndex] === queryLower[queryIndex]) {
      queryIndex++;
    }
    textIndex++;
  }
  return queryIndex === queryLower.length;
}

// 搜索和排序 conversations
export function searchAndSortConversations(
  conversations: Conversation[],
  query: string
): Conversation[] {
  if (!query.trim()) {
    return conversations;
  }
  const substringMatches: Conversation[] = [];
  const subsequenceMatches: Conversation[] = [];
  conversations.forEach(conv => {
    if (substringMatch(conv.title, query)) {
      substringMatches.push(conv);
    } else if (subsequenceMatch(conv.title, query)) {
      subsequenceMatches.push(conv);
    }
  });
  return [...substringMatches, ...subsequenceMatches];
}
export function generateId(): string {
  // Generate UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
export function formatTimestamp(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  // Less than 1 minute
  if (diff < 60000) {
    return 'just now';
  }
  // Less than 1 hour
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes}m ago`;
  }
  // Less than 24 hours
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  }
  // Less than 7 days
  if (diff < 604800000) {
    const days = Math.floor(diff / 86400000);
    return `${days}d ago`;
  }
  // Format as date
  return date.toLocaleDateString();
}
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}
export function extractFileName(filePath: string) {
  if (!filePath) return '';
  const parts = filePath.split(/[/\\]/);
  return parts[parts.length - 1] || filePath;
}
export function parseSearchParams(search: string): Record<string, string> {
  const params: Record<string, string> = {};
  // Remove leading '?' if present
  const searchString = search.startsWith('?') ? search.slice(1) : search;
  // If empty, return empty object
  if (!searchString) return params;
  // Split by '&' and parse each key=value pair
  searchString.split('&').forEach(pair => {
    const [key, value] = pair.split('=');
    if (key) {
      // Decode URI components to handle special characters
      params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
    }
  });
  return params;
}
export function formatDate(date: string): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  return new Date(date).toLocaleDateString('en-US', options);
}

export function formatDateTime(dateOrString: Date | string): string {
  let date: Date;

  if (typeof dateOrString === 'string') {
    // Parse the string as UTC time
    date = new Date(dateOrString);
  } else {
    date = dateOrString;
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };

  // Use 'en-US' locale with hour12: false for 24-hour format
  const result = date.toLocaleString('en-US', options);
  return result;
}
// Global 401 error handler
let isRedirecting = false;
let loginCompletionPromise: Promise<void> | null = null;
let resolveLoginCompletion: (() => void) | null = null;
let globalUnauthorizedHandler: ((redirectUrl?: string) => void) | null = null;
export function setGlobalUnauthorizedHandler(
  handler: (redirectUrl?: string) => void
) {
  globalUnauthorizedHandler = handler;
}
/**
 * 标记登录流程开始
 * 在登录完成前，所有 API 请求都会等待
 */
export function startLoginProcess() {
  loginCompletionPromise = new Promise(resolve => {
    resolveLoginCompletion = resolve;
  });
}
/**
 * 标记登录流程完成
 * 释放所有等待的 API 请求
 */
export function completeLoginProcess() {
  if (resolveLoginCompletion) {
    resolveLoginCompletion();
    resolveLoginCompletion = null;
  }
  loginCompletionPromise = null;
}
/**
 * 检查是否正在登录，如果是则等待登录完成
 */
export async function waitForLoginCompletion() {
  if (loginCompletionPromise) {
    await loginCompletionPromise;
  }
}
/**
 * 检查是否正在登录流程中
 */
export function isLoginInProgress(): boolean {
  return loginCompletionPromise !== null;
}
export function handleUnauthorized(redirects: any) {
  // 如果正在登录流程中，忽略 401 错误
  // 这些错误通常是因为 token 刚刚设置，后端 session 还没有完全同步
  if (isLoginInProgress()) {
    return;
  }
  if (!isRedirecting) {
    isRedirecting = true;
    // Clear any stored authentication data
    localStorage.removeItem('user-token');
    localStorage.removeItem('isLoggedIn');
    const redirect: any = redirects?.find((it: any) => it.name === 'microsoft');
    // Call the global handler if available
    if (globalUnauthorizedHandler) {
      globalUnauthorizedHandler(redirect?.redirectUrl || '');
    }
    // Reset the flag after a short delay to allow for potential retries
    setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  }
}
/**
 * @deprecated 使用 @/lib/api 模块中的 apiGet/apiPost 等方法替代
 *
 * Enhanced fetch wrapper with automatic 401 handling and Bearer token
 *
 * 此函数已废弃，请使用新的统一 API 模块：
 * - import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '@/lib/api';
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  console.warn(
    '⚠️ fetchWithAuth is deprecated. Please use apiGet/apiPost from @/lib/api instead.'
  );
  // 等待登录完成（如果正在登录）
  await waitForLoginCompletion();
  // Get token from localStorage
  const token = localStorage.getItem('user-token');
  // Prepare headers with Bearer token if available
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Merge the new headers with existing init
  const enhancedInit: RequestInit = {
    ...init,
    headers,
  };
  try {
    const response = await fetch(input, enhancedInit);
    // ONLY handle 401 errors - do not handle timeouts, network errors, or other issues
    if (response.status === 401) {
      try {
        const resData = await response.json();
        handleUnauthorized(resData?.redirects);
      } catch (jsonError) {
        // If parsing response body fails, still trigger unauthorized handler
        console.error('Failed to parse 401 response body:', jsonError);
        handleUnauthorized(undefined);
      }
      throw new Error('Unauthorized - redirecting to login');
    }
    return response;
  } catch (error) {
    // Only rethrow the error - do NOT trigger unauthorized handler for network errors or timeouts
    // This ensures we only redirect to login on actual 401 responses
    if (
      error instanceof Error &&
      error.message === 'Unauthorized - redirecting to login'
    ) {
      throw error;
    }
    // For network errors, timeouts, etc., log and rethrow without triggering logout
    console.error('fetchWithAuth: Network error or timeout:', error);
    throw error;
  }
}

/**
 * @deprecated 使用 @/lib/api 模块中的 apiGet/apiPost 等方法替代
 *
 * A convenience wrapper around `fetchWithAuth` that parses JSON and
 * shows a toast when the response is OK but the returned payload
 * contains a non-200 `code` field. Returns the parsed JSON (if any).
 *
 * 此函数已废弃，请使用新的统一 API 模块：
 * - import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '@/lib/api';
 */
import { toast } from '@/hooks/ui/use-toast';
export async function fetchJson(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<any> {
  console.warn(
    '⚠️ fetchJson is deprecated. Please use apiGet/apiPost from @/lib/api instead.'
  );
  const response = await fetchWithAuth(input, init);
  if (response.ok) {
    let data: any = null;
    try {
      data = await response.json();
    } catch (e) {
      // No JSON body
      return null;
    }
    // If API uses { code, message } convention, show toast on non-200
    if (data && typeof data.code !== 'undefined' && data.code !== 200) {
      toast({
        title: 'Error',
        description: data.message || 'Request failed',
        variant: 'destructive',
      });
    }
    return data;
  }
  // For non-ok responses, let caller handle parsing/error as before
  return response;
}
